import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Dispatch, JSX, SetStateAction, useState, useEffect, useRef } from "react";
import { useForm, SubmitHandler, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TAddCarSchema, AddCarSchema } from "../Models";
import { useAddCarMutationHook, useRemoveCarMutationHook, useRebalanceCarRatiosMutationHook } from "@/hooks";
import { useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormattedNumberInputField, CustomToastComponent, InputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import ServiceRatioSelect from "../Components/ServiceRatioSelect";
import StepIndicator from "../Components/StepIndicator";
import { CarDetails, ServiceRatioInput, ServiceType } from "@/graphql/__generated__/graphql";
import { convertDecimalToBp, convertBpToDecimal } from "@/utils/helpers";
import { toast } from "react-toastify";
import { Form } from "@/components/ui/form";

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  carDetails: CarDetails | null;
}

/**
 * EditCarDialog component for editing car details.
 * Uses delete-create pattern: removes old car and creates new one with updated values.
 *
 * Step 1: Edit car details
 * Step 2: Edit linked services
 *
 * @param {Props} props
 * @returns {JSX.Element}
 */
const EditCarDialog = (props: Props): JSX.Element => {
  const { open, onOpenChange, carDetails } = props;

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Hooks
  const { t } = useTranslation(["modals", "common", "translation", "toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Query hooks
  const { data: servicesData, loading: servicesLoading } = useFetchServicesOnTenderQueryHook(
    Number(tenderID),
    false,
    true
  );

  // Mutation hooks - disable toasts since we handle our own toast for edit operation
  const { addCarMutation, loading: addCarLoading } = useAddCarMutationHook({
    disableToast: true,
  });
  const { removeCarMutation, loading: removeCarLoading } = useRemoveCarMutationHook({
    disableToast: true,
    disableRefetch: true,
  });
  const { rebalanceCarRatiosMutation, loading: rebalanceLoading } = useRebalanceCarRatiosMutationHook({
    disableToast: true,
  });

  // Store initial values for comparison
  const initialValuesRef = useRef<TAddCarSchema | null>(null);

  // Get default values from existing car
  const getDefaultValues = (car: CarDetails | null): TAddCarSchema => {
    if (!car) {
      return {
        name: "",
        netValue: 0,
        runtime: 0,
        yearlyInsurance: 0,
        yearlyTax: 0,
        yearlyKilometers: 0,
        consumption: 0,
        fuelPrice: 0,
        repairPerKilometer: 0,
        quantity: 0,
        margin: 0,
      };
    }
    return {
      name: car.car.name || "",
      netValue: car.car.netValue,
      runtime: car.car.runtime,
      yearlyInsurance: car.car.yearlyInsurance,
      yearlyTax: car.car.yearlyTax,
      yearlyKilometers: car.car.yearlyKilometers,
      consumption: car.car.consumption,
      fuelPrice: car.car.fuelPrice,
      repairPerKilometer: car.car.repairPerKilometer,
      quantity: car.car.quantity,
      margin: convertBpToDecimal(car.car.margin),
    };
  };

  // Init form object
  const form = useForm<TAddCarSchema>({
    resolver: zodResolver(AddCarSchema) as Resolver<TAddCarSchema>,
    mode: "onSubmit",
    defaultValues: getDefaultValues(carDetails),
  });

  // State for service ratios
  const [selectedServices, setSelectedServices] = useState<ServiceRatioInput[]>([]);

  // Initialize form and service ratios when dialog opens or carDetails changes
  useEffect(() => {
    if (open && carDetails) {
      const defaults = getDefaultValues(carDetails);
      form.reset(defaults);
      // Store initial values for comparison
      initialValuesRef.current = defaults;
      // Convert existing links from basis points to percentage for display
      if (carDetails.links && carDetails.links.length > 0) {
        const existingLinks = carDetails.links.map((link) => ({
          serviceId: link.serviceId,
          ratio: convertBpToDecimal(link.ratio),
        }));
        setSelectedServices(existingLinks);
      } else {
        setSelectedServices([]);
      }
    }
  }, [open, carDetails, form]);

  /**
   * Check if only service ratios have changed (car details remain the same)
   */
  const hasOnlyRatiosChanged = (newData: TAddCarSchema): boolean => {
    if (!initialValuesRef.current) return false;

    const initial = initialValuesRef.current;

    return (
      initial.name === newData.name &&
      Number(initial.netValue) === Number(newData.netValue) &&
      Number(initial.runtime) === Number(newData.runtime) &&
      Number(initial.yearlyInsurance) === Number(newData.yearlyInsurance) &&
      Number(initial.yearlyTax) === Number(newData.yearlyTax) &&
      Number(initial.yearlyKilometers) === Number(newData.yearlyKilometers) &&
      Number(initial.consumption) === Number(newData.consumption) &&
      Number(initial.fuelPrice) === Number(newData.fuelPrice) &&
      Number(initial.repairPerKilometer) === Number(newData.repairPerKilometer) &&
      Number(initial.quantity) === Number(newData.quantity) &&
      Number(initial.margin) === Number(newData.margin)
    );
  };

  // Get services for the dropdown (only permanent services, as mobile services cannot have cars)
  const availableServices =
    servicesData?.fetchServicesOnTender?.services
      ?.filter((service) => service.type !== ServiceType.Mobile && service.additional === false)
      .map((service) => ({
        id: service.id,
        name: service.name,
        operationalOrganizationShortName: service.operationalOrganizationShortName,
      })) ?? [];

  // Calculate if service ratios are valid (either empty or sum to 100)
  const totalRatio = selectedServices.reduce((sum, s) => sum + s.ratio, 0);
  const isRatioValid = selectedServices.length === 0 || totalRatio === 100;

  const inputFieldsData = [
    {
      name: "netValue",
      label: t("addcar.inputs.netvalue.label"),
      placeholder: t("addcar.inputs.netvalue.placeholder"),
      style: "currency",
    },
    {
      name: "runtime",
      label: t("addcar.inputs.runtime.label"),
      placeholder: t("addcar.inputs.runtime.placeholder"),
      isInteger: true,
    },
    {
      name: "yearlyInsurance",
      label: t("addcar.inputs.yearlyinsurance.label"),
      placeholder: t("addcar.inputs.yearlyinsurance.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyTax",
      label: t("addcar.inputs.yearlytax.label"),
      placeholder: t("addcar.inputs.yearlytax.placeholder"),
      style: "currency",
    },
    {
      name: "yearlyKilometers",
      label: t("addcar.inputs.yearlykilometers.label"),
      placeholder: t("addcar.inputs.yearlykilometers.placeholder"),
    },
    {
      name: "consumption",
      label: t("addcar.inputs.consumption.label"),
      placeholder: t("addcar.inputs.consumption.placeholder"),
    },
    {
      name: "fuelPrice",
      label: t("addcar.inputs.fuelprice.label"),
      placeholder: t("addcar.inputs.fuelprice.placeholder"),
      style: "currency",
      additionalSuffix: " / L",
    },
    {
      name: "repairPerKilometer",
      label: t("addcar.inputs.repairperkm.label"),
      placeholder: t("addcar.inputs.repairperkm.placeholder"),
      style: "currency",
      additionalSuffix: " / km",
    },
    {
      name: "quantity",
      label: t("addcar.inputs.quantity.label"),
      placeholder: t("addcar.inputs.quantity.placeholder"),
      isInteger: true,
    },
    {
      name: "margin",
      label: t("addcar.inputs.margin.label"),
      placeholder: t("addcar.inputs.margin.placeholder"),
      additionalSuffix: "%",
    },
  ];

  /**
   * Handle navigation to next step
   */
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep === 1) {
      // Validate step 1 (form fields)
      const isValid = await form.trigger();
      if (isValid) {
        setCurrentStep(2);
      }
    }
  };

  /**
   * Handle navigation to previous step
   */
  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Handler function for editing a car.
   * If only ratios changed, use rebalance mutation.
   * Otherwise, use delete-create pattern.
   * @param {TAddCarSchema} data
   */
  const onSubmit: SubmitHandler<TAddCarSchema> = async (data: TAddCarSchema) => {
    if (!carDetails || !isRatioValid) return;

    setIsProcessing(true);

    try {
      // Convert service ratios from percentage (0-100) to basis points (0-10000)
      const servicesInBp = selectedServices.map((s) => ({
        ...s,
        ratio: convertDecimalToBp(s.ratio),
      }));

      // Check if only ratios changed - if so, use rebalance mutation
      if (hasOnlyRatiosChanged(data)) {
        const result = await rebalanceCarRatiosMutation({
          variables: {
            tenderId: Number(tenderID),
            carId: carDetails.car.id,
            linkedServices: servicesInBp,
          },
        });

        if (!result.error) {
          toast(CustomToastComponent, {
            toastId: "car-edited-success",
            data: {
              title: t("toasts:toasts.caredited"),
              type: "success",
            },
          });

          // Reset form and close dialog
          setSelectedServices([]);
          setCurrentStep(1);
          onOpenChange(false);
        }
      } else {
        // Car details changed - use delete-create pattern
        // Step 1: Remove the old car
        await removeCarMutation({
          variables: {
            tenderId: Number(tenderID),
            carId: carDetails.car.id,
          },
        });

        // Step 2: Create new car with updated values
        form.clearErrors();

        // Convert margin from percentage to basis points
        const inputData = {
          ...data,
          margin: convertDecimalToBp(data.margin),
        };

        const result = await addCarMutation({
          variables: {
            tenderId: Number(tenderID),
            linkedServices: servicesInBp,
            input: inputData,
          },
        });

        if (!result.errors) {
          toast(CustomToastComponent, {
            toastId: "car-edited-success",
            data: {
              title: t("toasts:toasts.caredited"),
              type: "success",
            },
          });

          // Reset form and close dialog
          setSelectedServices([]);
          setCurrentStep(1);
          onOpenChange(false);
        }
      }
    } catch (error) {
      toast(CustomToastComponent, {
        toastId: "car-edit-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: (error as Error).message || t("toasts:toasts.pleasetryagain"),
          type: "error",
        },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedServices([]);
      setCurrentStep(1);
      form.reset(getDefaultValues(null));
    }
    onOpenChange(newOpen);
  };

  const isLoading = isProcessing || addCarLoading || removeCarLoading || rebalanceLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[90vw] lg:max-w-2xl overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <Form {...form}>
          <form className="space-y-6" id="edit-car-form" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t("editcar.title")}</DialogTitle>
              <DialogDescription>{t("editcar.fulldescription")}</DialogDescription>
            </DialogHeader>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} totalSteps={2} />

            {/* Step 1: Car Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step1description")}
                </h3>
                <div className="flex-wrap-4">
                  <div className="form-container">
                    <InputField
                      form={form}
                      name="name"
                      label={t("addcar.inputs.name.label")}
                      placeholder={t("addcar.inputs.name.placeholder")}
                      className="w-full"
                    />
                  </div>
                  {inputFieldsData.map((fieldData) => (
                    <div key={fieldData.name} className="form-container">
                      <FormattedNumberInputField
                        name={fieldData.name}
                        control={form.control}
                        additionalSuffix={fieldData.additionalSuffix}
                        placeholder={fieldData.placeholder}
                        style={fieldData.style as "currency" | "decimal"}
                        isInteger={fieldData.isInteger}
                        isStandalone={true}
                        label={fieldData.label}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Link to Services */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("translation:stepper.step2description")}
                </h3>
                <ServiceRatioSelect
                  services={availableServices}
                  selectedServices={selectedServices}
                  onServicesChange={setSelectedServices}
                  disabled={isLoading}
                  loading={servicesLoading}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <DialogFooter className="flex justify-between sm:justify-between">
              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack} disabled={isLoading}>
                    {t("common:buttons.back")}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isLoading}
                >
                  {t("common:buttons.cancel")}
                </Button>
                {currentStep < 2 ? (
                  <Button type="button" onClick={handleNext}>
                    {t("common:buttons.next")}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isLoading || !isRatioValid} className="gap-2">
                    <CircleLoader visible={isLoading} height={16} width={16} strokeWidth={2} />
                    {t("common:buttons.save")}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCarDialog;
